import KeyInformation from '@/pages/CompanyReport/components/Introduction/components/KeyInformation';
import { useMemo } from 'react';
import { useBankMetrics } from '@/pages/CompanyReport/components/Health/hooks';
import { addBadge } from './utils';
import { Heading } from '@simplywallst/ui-core';
import { useHealthTranslations } from '../../../../hooks/useHealthTranslations';
type Props = { canonicalURL: string };

function getKeyFromKeyMetric(keyMetric: string) {
  switch (keyMetric) {
    case 'Asset to equity ratio':
      return 'keyInfo.metric.bank.assetToEquityRatio';
    case 'Net interest margin':
      return 'keyInfo.metric.bank.netInterestMargin';
    case 'Total deposits':
      return 'keyInfo.metric.bank.totalDeposits';
    case 'Loan to deposit ratio':
      return 'keyInfo.metric.bank.loanDepositRatio';
    case 'Bad loans':
      return 'keyInfo.metric.bank.badLoans';
    case 'Allowance for bad loans':
      return 'keyInfo.metric.bank.allowanceForBadLoans';
    case 'Current ratio':
      return 'keyInfo.metric.bank.currentRatio';
    case 'Cash & equivalents':
      return 'keyInfo.metric.bank.cashEquivalents';
  }
}

function getKeyFromValue(value: string) {
  switch (value) {
    case 'Appropriate':
      return 'badge.appropriate';
    case 'Sufficient':
      return 'badge.sufficient';
    case 'Good':
      return 'badge.good';
    case 'High':
      return 'badge.high';
    case 'Low':
      return 'badge.low';
    case 'None':
      return 'badge.none';
  }
}

function getBadgeStyle(value: string) {
  switch (value) {
    case 'Appropriate':
    case 'Sufficient':
    case 'Good':
      return 'positive';
    case 'High':
    case 'Low':
      return 'warning';
    default:
      return 'none';
  }
}

export const KeyInformationBank = ({ canonicalURL }: Props) => {
  const { keyMetrics, metrics } = useBankMetrics(canonicalURL);

  const t = useHealthTranslations();

  const translatedKeyMetrics = useMemo(() => {
    return keyMetrics.map((keyMetric) => {
      const translatedTitle = getKeyFromKeyMetric(keyMetric.title);
      return {
        ...keyMetric,
        title: translatedTitle ? t(translatedTitle) : keyMetric.title,
      };
    }) as typeof keyMetrics; // need to cast here because the map function is not smart enough to know that the return type is the same as the input type
  }, [keyMetrics, t]);

  const translatedMetric = useMemo(() => {
    return metrics.map((metric) => {
      const translatedTitle = getKeyFromKeyMetric(metric.title);
      const valueKey = getKeyFromValue(metric.value);
      return {
        ...metric,
        title: translatedTitle ? t(translatedTitle) : metric.title,
        value: valueKey ? t(valueKey) : metric.value,
        styleType: getBadgeStyle(metric.value),
      } as const;
    });
  }, [metrics, t]);
  const metricsWithBadge = useMemo(
    () => addBadge(translatedMetric),
    [translatedMetric]
  );
  return (
    <KeyInformation
      direction="row"
      keyMetrics={translatedKeyMetrics}
      metrics={metricsWithBadge}
      heading={
        <Heading as="h3" typography="small">
          {t('keyInformation')}
        </Heading>
      }
    />
  );
};
